/**
 * Environment Safety Module
 *
 * Provides runtime detection and logging for environment mismatches that could
 * indicate misconfiguration. This is a defense-in-depth measure that helps
 * identify when a preview/staging deployment accidentally connects to production.
 */
/**
 * Checks if a URL is a production API URL (not sandbox or test).
 */
function isProductionApiUrl(url) {
    if (!url)
        return false;
    return (url.includes('api.tinyhealth.com') &&
        !url.includes('sandbox-api') &&
        !url.includes('test-tinyhealth'));
}
/**
 * Checks if an origin is a production origin (not test domain).
 */
function isProductionOrigin(origin) {
    return (origin.includes('tinyhealth.com') &&
        !origin.includes('test-tinyhealth') &&
        !origin.includes('localhost') &&
        !origin.includes('vercel.app'));
}
/**
 * Validates environment safety and detects potential mismatches.
 *
 * This function checks for common misconfigurations:
 * 1. Using production API from non-production environment
 * 2. Using production API from non-production origin (domain)
 *
 * @param config - The environment configuration to validate
 * @returns Result with validation status and any warnings/errors
 */
export function validateEnvironmentSafety(config) {
    const { environment, apiUrl, appOrigin } = config;
    const warnings = [];
    const errors = [];
    const usingProductionApi = isProductionApiUrl(apiUrl);
    const isProductionEnv = environment === 'production';
    const originIsProduction = appOrigin ? isProductionOrigin(appOrigin) : null;
    /*
     * Check 1: Production API with non-production environment variable
     * This is a strong indicator of misconfiguration
     */
    if (usingProductionApi && !isProductionEnv) {
        errors.push(`[ENVIRONMENT MISMATCH] Using production API (${apiUrl}) but ` +
            `environment is "${environment || 'undefined'}". ` +
            `This may indicate CI/CD misconfiguration. ` +
            `Expected environment="production" when using production API.`);
    }
    /*
     * Check 2: Production API from non-production origin
     * This catches cases where the app is served from a test/preview domain
     * but is configured to hit production API
     */
    if (usingProductionApi && appOrigin && originIsProduction === false) {
        errors.push(`[ENVIRONMENT MISMATCH] Using production API (${apiUrl}) from ` +
            `non-production origin (${appOrigin}). ` +
            `This is a potential data safety issue - preview/staging apps ` +
            `should not connect to production backend.`);
    }
    /*
     * Check 3: Environment undefined or empty
     * This is a warning - the fallback logic will use sandbox, but it's
     * better to have explicit configuration
     */
    if (!environment) {
        warnings.push(`[ENVIRONMENT WARNING] Environment variable is not set (value: "${environment}"). ` +
            `API client will fall back to sandbox API for safety. ` +
            `Set VITE_APP_ENVIRONMENT explicitly for clarity.`);
    }
    return {
        isValid: errors.length === 0,
        warnings,
        errors,
    };
}
/**
 * Logs environment safety validation results to the console.
 *
 * @param result - The validation result to log
 */
export function logEnvironmentSafetyResult(result) {
    // Log warnings
    result.warnings.forEach(warning => {
        console.warn(warning);
    });
    // Log errors
    result.errors.forEach(error => {
        console.error(error);
    });
    // Log success if no issues
    if (result.isValid && result.warnings.length === 0) {
        console.log('[Environment Safety] Configuration validated successfully');
    }
}
/**
 * Convenience function that validates and logs in one call.
 *
 * @param config - The environment configuration to validate
 * @returns The validation result
 */
export function checkEnvironmentSafety(config) {
    const result = validateEnvironmentSafety(config);
    logEnvironmentSafetyResult(result);
    return result;
}
//# sourceMappingURL=environment-safety.js.map